/**
 * Composio Connection Service
 *
 * Provides a unified connect flow for all Composio-managed integrations.
 * Replaces per-service OAuth flows (GitHub, Gmail, Google Calendar, Spotify, etc.).
 *
 * Flow:
 *   1. Call POST /api/composio/connect to get a Composio Connect Link URL
 *   2. Open the URL in a centered popup
 *   3. Poll until the popup closes (Composio handles the OAuth redirect internally)
 *   4. Return control to the caller — Composio has stored the tokens
 */

import { API_BASE_URL } from '../api/config';
import { createFileLogger } from '../utils/logger';
import { undismiss } from '../utils/dismissedIntegrations';

const log = createFileLogger('ComposioService');

/**
 * Fired after every successful Composio OAuth connection.
 * Listeners (chat cards, Settings panel) use this to update their state
 * without coupling directly to each other.
 */
export interface ComposioConnectedEvent extends CustomEvent {
  detail: { slug: string };
}
export const COMPOSIO_CONNECTED_EVENT = 'composio-integration-connected';

/**
 * Maps our internal integration type names to Composio toolkit slugs.
 * Composio uses lowercase identifiers; see https://app.composio.dev/toolkits
 */
export const COMPOSIO_TOOLKIT_MAP: Record<string, string> = {
  // Google
  Gmail:           'gmail',
  GoogleCalendar:  'googlecalendar',
  GoogleDrive:     'googledrive',
  GoogleAnalytics: 'googleanalytics',

  // Developer
  GitHub:  'github',

  // Music / Media
  Spotify: 'spotify',

  // Communication
  Slack:   'slack',
};

export interface ComposioConnectResult {
  toolkit: string;
  redirect_url: string;
}

/**
 * Generate a Composio Connect Link for the given toolkit.
 * Returns the redirect_url the user must visit to authorize.
 */
async function getAuthToken(): Promise<string> {
  const token = localStorage.getItem('authToken');
  if (!token) throw new Error('Not authenticated — please log in and try again.');
  return token;
}

async function getConnectLink(toolkit: string): Promise<string> {
  const authToken = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/composio/connect`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ toolkit }),
  });

  if (!response.ok) {
    let detail = `Failed to get Composio Connect Link: HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body.detail) {
        detail = typeof body.detail === 'string' ? body.detail : String(body.detail);
      }
    } catch {
      /* keep generic detail */
    }
    if (response.status === 503) {
      const looksUnconfigured =
        /not configured|composio.*api.*key/i.test(detail) ||
        detail.includes('Composio is not configured');
      if (looksUnconfigured) {
        throw new Error(
          'Composio is not configured: set COMPOSIO_API_KEY on the API backend (key from app.composio.dev), restart it, and set the same variable on tool-runtime for agent integrations. Then try Connect again.',
        );
      }
    }
    throw new Error(detail);
  }

  const data = await response.json();
  return data.redirect_url;
}

export interface ConnectComposioToolkitOptions {
  timeoutMs?: number;
  /** Pre-opened popup from the same user gesture (Safari blocks window.open after await). */
  reusePopup?: Window;
}

/**
 * Connect a toolkit via Composio by opening a popup and waiting for it to close.
 *
 * @param toolkit  Composio toolkit slug (e.g. "github", "gmail") or a mapped
 *                 integration type name (e.g. "GitHub", "Gmail").
 * @param optionsOrTimeout  Options, or legacy timeout in ms (default 5 min).
 */
export async function connectComposioToolkit(
  toolkit: string,
  optionsOrTimeout?: number | ConnectComposioToolkitOptions,
): Promise<void> {
  // Normalise: if caller passes our type name, map to Composio slug
  const slug = COMPOSIO_TOOLKIT_MAP[toolkit] ?? toolkit.toLowerCase();

  log.log(`[Composio] Connecting toolkit: ${slug}`);

  let timeoutMs = 5 * 60 * 1000;
  let reusePopup: Window | undefined;
  if (typeof optionsOrTimeout === 'number') {
    timeoutMs = optionsOrTimeout;
  } else if (optionsOrTimeout && typeof optionsOrTimeout === 'object') {
    timeoutMs = optionsOrTimeout.timeoutMs ?? timeoutMs;
    reusePopup = optionsOrTimeout.reusePopup;
  }

  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

  const popup =
    reusePopup ??
    window.open('about:blank', `ComposioAuth_${slug}`, features);
  if (!popup) {
    throw new Error('Failed to open authorization popup. Please allow popups for this site.');
  }

  let redirectUrl: string;
  try {
    redirectUrl = await getConnectLink(slug);
  } catch (e) {
    popup.close();
    throw e;
  }

  popup.location.href = redirectUrl;

  await new Promise<void>((resolve, reject) => {
    let checkInterval: ReturnType<typeof setInterval> | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (checkInterval) { clearInterval(checkInterval); checkInterval = null; }
      if (timeoutHandle) { clearTimeout(timeoutHandle); timeoutHandle = null; }
    };

    // Poll for popup closure
    checkInterval = setInterval(() => {
      try {
        if (popup.closed) {
          cleanup();
          log.log(`[Composio] Popup closed for toolkit: ${slug}`);
          resolve();
        }
      } catch {
        // Cross-origin access throws — popup is closed
        cleanup();
        resolve();
      }
    }, 500);

    timeoutHandle = setTimeout(() => {
      cleanup();
      try { if (!popup.closed) popup.close(); } catch {}
      reject(new Error('Authorization timed out — please try again.'));
    }, timeoutMs);
  });

  log.log(`[Composio] ✅ Toolkit connected: ${slug}`);

  // Clear any "Don't suggest again" dismissal so the agent can freely use
  // and suggest this integration now that it's actually connected.
  undismiss(slug);

  // Notify any mounted listeners (chat cards, Settings panel) that this toolkit
  // just connected. This lets a chat setup_integration card resume the agent
  // even when the user completed OAuth from the Settings page instead.
  window.dispatchEvent(
    new CustomEvent(COMPOSIO_CONNECTED_EVENT, { detail: { slug } }),
  );
}

/**
 * List all Composio connected accounts for the current user.
 * Used to display connection status in settings.
 */
export async function listComposioConnections(): Promise<any[]> {
  const authToken = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/composio/connected`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });

  if (!response.ok) return [];

  const data = await response.json();
  return Array.isArray(data) ? data : (data.items ?? data.connections ?? []);
}

/**
 * Disconnect a Composio connected account.
 */
export async function disconnectComposioAccount(connectedAccountId: string): Promise<void> {
  const authToken = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/composio/disconnect`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ connected_account_id: connectedAccountId }),
  });

  if (!response.ok) {
    let detail = `Disconnect failed: HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body.detail) detail = body.detail;
    } catch {}
    throw new Error(detail);
  }
}
